import { useSnackbar } from "notistack";
import { FC, useCallback, useEffect, useRef, useState } from "react";

import { Button, FormControl, IconButton, MenuItem, Select, Stack } from "@mui/material";

import AddIcon from '@mui/icons-material/Add';

import BoothComponent from "./BoothComponent";

import { api } from "../../../../../utils";
import { IError } from "../../../../../models/ReportPageModels";

interface BoothIdStructure {
    data: BoothIdStructureData;
    error: IError;
}
interface BoothIdStructureData {
    structure: BoothFilterModel[];
}
interface BoothFilterModel {
    databaseName: string;
    boothIds: BoothClientModel[];
}
export interface BoothClientModel {
    boothId: string;
    name: string;
    location: string;
}

interface OtherBoothIds {
    data: OtherBoothIdsData;
    error: IError;
}
interface OtherBoothIdsData {
    boothIds: BoothClientModel[];
}

interface SaveBoothIdsRequest {
    database: string;
    boothIds: BoothClientModel[];
}
interface SaveBoothIds {
    data: SaveBoothIdsData;
    error: IError;
}
interface SaveBoothIdsData {
    status: string;
}

interface UpdateBoothIdProps {
    workflowNextStep?: () => void;
    createdDatabase?: string | null;
}

const UpdateBoothId: FC<UpdateBoothIdProps> = ({ workflowNextStep = null, createdDatabase = null }) => {
    const { enqueueSnackbar } = useSnackbar();

    const structure = useRef<BoothFilterModel[]>([]);
    const [databaseNames, setDatabaseNames] = useState<string[]>([]);

    const [selectedDatabase, setSelectedDatabase] = useState<string>("");
    const [boothIds, setBoothIds] = useState<BoothClientModel[]>([]);

    const [otherBoothIds, setOtherBoothIds] = useState<BoothClientModel[]>([]);

    useEffect(() => {
        GetStructure();
    }, [])

    const GetStructure = async () => {
        try {
            const response: BoothIdStructure = await api.get("api/Admin/GetBoothStructure", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                structure.current = response.data.structure;

                const names: string[] = [];
                for (var i = 0; i < response.data.structure.length; i++) {
                    names.push(response.data.structure[i].databaseName);
                }
                setDatabaseNames(names);

                // database created in workflow previous step
                if (createdDatabase) {
                    setSelectedDatabase(createdDatabase);
                    for (let i = 0; i < response.data.structure.length; i++) {
                        if (response.data.structure[i].databaseName === createdDatabase) {
                            setBoothIds(response.data.structure[i].boothIds);
                            break;
                        }
                    }
                }
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function SetSelectedDatabase(event: any) {
        var value = event.target.value;
        setSelectedDatabase(value);

        setOtherBoothIds([]);
        for (let i = 0; i < structure.current.length; i++) {
            if (structure.current[i].databaseName === value) {
                setBoothIds(structure.current[i].boothIds);
                break;
            }
        }
    }

    const GetOtherBoothIds = async () => {
        setOtherBoothIds([]);

        if (selectedDatabase === "") {
            enqueueSnackbar("Please select the database", { variant: "error" });
            return;
        }

        try {
            const response: OtherBoothIds = await api.get(`api/Admin/GetOtherBoothIds?database=${selectedDatabase}`, {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setOtherBoothIds(response.data.boothIds);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function AddBoothFromOther(booth: BoothClientModel) {
        setBoothIds(current => [...current, booth]);
    }

    function AddEmptyBoothId() {
        const newBooth: BoothClientModel = {
            boothId: "",
            name: "",
            location: ""
        }
        setBoothIds(current => [...current, newBooth])
    }

    const SaveBoothIds = async () => {
        if (selectedDatabase === "") {
            enqueueSnackbar("Please select the database", { variant: "error" });
            return;
        }

        try {
            var body: SaveBoothIdsRequest = {
                database: selectedDatabase,
                boothIds: boothIds
            }

            const response: SaveBoothIds = await api.post("api/Admin/SaveBoothIds", {
                json: body,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                enqueueSnackbar("Saved!", { variant: "success" });

                // for workflow function to step passed
                if (workflowNextStep)
                    workflowNextStep();
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const RemoveBoothId = useCallback((booth: BoothClientModel) => {
        var temp: BoothClientModel[] = [];
        for (let i = 0; i < boothIds.length; i++) {
            if (booth.name === boothIds[i].name && booth.boothId === boothIds[i].boothId)
                continue;
            temp.push(boothIds[i]);
        }
        setBoothIds(temp);
    }, [boothIds])

    const DatabasesSavePanelHeight = 173;
    const WorkflowPanelHeight = 130;
    function GetPanelHeight() {
        if (workflowNextStep)
            return window.innerHeight - WorkflowPanelHeight - DatabasesSavePanelHeight;

        return window.innerHeight - DatabasesSavePanelHeight;
    }

    return (
        <Stack>
            {/* Database names */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Databases: </p>
                <FormControl sx={{ minWidth: "230px", width: "230px" }}>
                    <Select
                        value={selectedDatabase}
                        onChange={SetSelectedDatabase}
                        size="small"
                        defaultValue={""}>
                        {databaseNames.map((database: string, index) =>
                            <MenuItem
                                key={index}
                                value={database}>
                                {database}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
                <Button variant="outlined"
                    onClick={SaveBoothIds}
                    sx={{ width: "50px", marginLeft: "10px" }}>
                    Save
                </Button>
            </Stack>

            <Stack direction={"row"} spacing={5}>
                <Stack direction='column'
                    sx={{ width: '300px', height: GetPanelHeight(), marginTop: '3px', overflowX: 'auto', overflowY: 'auto' }}>
                    {boothIds.map((booth: BoothClientModel, index) => (
                        <BoothComponent
                            key={index}
                            booth={booth}
                            remove={RemoveBoothId} />
                    ))}
                    {selectedDatabase !== "" ?
                        <Button variant="outlined"
                            onClick={AddEmptyBoothId}
                            sx={{ width: "20px", marginLeft: "103px", fontSize: "16px" }}>
                            +
                        </Button>
                        : null}

                </Stack>
                <Stack>
                    <Button variant="outlined"
                        onClick={GetOtherBoothIds}
                        sx={{ width: "250px", marginLeft: "10px" }}>
                        Get BoothId from database
                    </Button>
                    <Stack direction='column'
                        sx={{ width: '500px', height: GetPanelHeight() - 36, marginTop: '3px', overflowX: 'auto', overflowY: 'auto' }}>
                        {otherBoothIds.map((booth: BoothClientModel) => (
                            <Stack
                                direction={"row"}
                                justifyContent="space-between"
                                alignItems="flex-start"
                                sx={{ marginBottom: "7px", padding: "5px", border: "1px black solid" }}>
                                <Stack>
                                    <span>Name:    {booth.name}</span>
                                    <span>BoothId: {booth.boothId}</span>
                                </Stack>
                                <IconButton
                                    color="primary"
                                    size="large"
                                    sx={{ width: "30px", height: "30px", marginTop: "10px" }}
                                    onClick={() => { AddBoothFromOther(booth) }}>
                                    <AddIcon />
                                </IconButton>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    )
}

export default UpdateBoothId;